import React, { useState } from 'react';
import { Button, Input } from '@sisumail/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AdminLog } from '../../hooks/useAdminData';

interface LogsTableProps {
    logs: AdminLog[];
    total: number;
    page: number;
    limit: number;
    onPageChange: (newPage: number) => void;
    // Filters
    source: string;
    onSourceChange: (val: string) => void;
    level: string;
    onLevelChange: (val: string) => void;
    traceId: string;
    onTraceIdChange: (val: string) => void;
    userId: string;
    onUserIdChange: (val: string) => void;
}

export function LogsTable({
    logs,
    total,
    page,
    limit,
    onPageChange,
    source,
    onSourceChange,
    level,
    onLevelChange,
    traceId,
    onTraceIdChange,
    userId,
    onUserIdChange,
}: LogsTableProps) {
    const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

    return (
        <div className="space-y-4">
            {/* Log Filters */}
            <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1.5">
                    <label
                        htmlFor="log-source"
                        className="ml-1 font-bold text-[10px] text-neutral-500 uppercase tracking-wider"
                    >
                        Source
                    </label>
                    <select
                        id="log-source"
                        value={source}
                        onChange={(e) => onSourceChange(e.target.value)}
                        className="h-10 rounded-lg border-neutral-700 bg-neutral-800 px-4 text-sm text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                        <option value="">All Sources</option>
                        <option value="api">API</option>
                        <option value="mail">Mail</option>
                        <option value="pwa">PWA</option>
                        <option value="app">App</option>
                        <option value="extension">Extension</option>
                        <option value="purge">Purge</option>
                    </select>
                </div>
                <div className="flex flex-col gap-1.5">
                    <label
                        htmlFor="log-level"
                        className="ml-1 font-bold text-[10px] text-neutral-500 uppercase tracking-wider"
                    >
                        Level
                    </label>
                    <select
                        id="log-level"
                        value={level}
                        onChange={(e) => onLevelChange(e.target.value)}
                        className="h-10 rounded-lg border-neutral-700 bg-neutral-800 px-4 text-sm text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                        <option value="">All Levels</option>
                        <option value="error">Error</option>
                        <option value="warn">Warning</option>
                        <option value="info">Info</option>
                        <option value="debug">Debug</option>
                    </select>
                </div>
                <Input
                    label="TraceID"
                    placeholder="Search TraceID..."
                    className="w-48"
                    value={traceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onTraceIdChange(e.target.value);
                    }}
                />
                <Input
                    label="User ID"
                    placeholder="User ID..."
                    className="w-32"
                    value={userId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onUserIdChange(e.target.value);
                    }}
                />
                <div className="mb-1 ml-auto flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => onPageChange(Math.max(0, page - 1))}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="min-w-[100px] text-center font-bold text-neutral-500 text-xs uppercase">
                        Page {page + 1} of {Math.ceil(total / limit) || 1}
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={(page + 1) * limit >= total}
                        onClick={() => onPageChange(page + 1)}
                    >
                        <ChevronRight size={16} />
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-800 bg-gray-950 shadow">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-900 text-gray-400 uppercase">
                        <tr>
                            <th className="px-6 py-4">Time</th>
                            <th className="px-6 py-4">Level</th>
                            <th className="px-6 py-4">Source</th>
                            <th className="px-6 py-4">Message</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 font-mono text-xs">
                        {logs.map((log) => (
                            <LogTableRow
                                key={log.id}
                                log={log}
                                isExpanded={expandedLogId === log.id}
                                onToggle={() =>
                                    setExpandedLogId(
                                        expandedLogId === log.id ? null : log.id
                                    )
                                }
                            />
                        ))}
                        {logs.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="p-8 text-center text-gray-500"
                                >
                                    No logs found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function LogTableRow({
    log,
    isExpanded,
    onToggle,
}: {
    log: AdminLog;
    isExpanded: boolean;
    onToggle: () => void;
}) {
    return (
        <>
            <tr className="cursor-pointer hover:bg-gray-900/50" onClick={onToggle}>
                <td className="whitespace-nowrap px-6 py-4 text-gray-400">
                    {new Date(log.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                    <span
                        className={`rounded px-2 py-0.5 font-medium text-xs ${log.level === 'error'
                            ? 'bg-red-900/50 text-red-400'
                            : log.level === 'warn'
                                ? 'bg-amber-900/50 text-amber-400'
                                : log.level === 'info'
                                    ? 'bg-blue-900/50 text-blue-400'
                                    : 'bg-gray-800 text-gray-400'
                            }`}
                    >
                        {log.level}
                    </span>
                </td>
                <td className="px-6 py-4">
                    <span
                        className={`rounded px-2 py-0.5 text-xs ${log.source === 'api'
                            ? 'bg-purple-900/50 text-purple-400'
                            : log.source === 'app'
                                ? 'bg-green-900/50 text-green-400'
                                : log.source === 'pwa'
                                    ? 'bg-cyan-900/50 text-cyan-400'
                                    : log.source === 'extension'
                                        ? 'bg-orange-900/50 text-orange-400'
                                        : 'bg-gray-800 text-gray-400'
                            }`}
                    >
                        {log.source}
                    </span>
                </td>
                <td className="max-w-md truncate px-6 py-4 text-gray-300">{log.message}</td>
            </tr>
            {isExpanded && log.metadata && (
                <tr key={`${log.id}-detail`}>
                    <td colSpan={4} className="bg-gray-900/50 px-6 py-4">
                        <pre className="max-h-64 overflow-x-auto whitespace-pre-wrap text-gray-300 text-xs">
                            {JSON.stringify(JSON.parse(log.metadata), null, 2)}
                        </pre>
                    </td>
                </tr>
            )}
        </>
    );
}
